import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { Calculator, Users, Monitor, ChevronDown, ChevronUp, Maximize2, LayoutGrid } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Persist Room Capacity inputs/results so the user can navigate away and come back
const STORAGE_KEY = "eventstack-room-capacity";
const loadStoredState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const RoomCapacity = () => {
  // Read sessionStorage on every fresh mount (lazy initialiser)
  const [length, setLength] = useState(() => loadStoredState()?.length ?? "");
  const [width, setWidth] = useState(() => loadStoredState()?.width ?? "");
  const [isMetric, setIsMetric] = useState(() => loadStoredState()?.isMetric ?? true);
  const [hasStagingAV, setHasStagingAV] = useState(() => loadStoredState()?.hasStagingAV ?? false);
  
  // Default values for metric and imperial
  const metricDefaults = {
    chairWidth: "0.5",
    chairDepth: "0.6",
    tableSize: "1.83",
    classroomTableLength: "1.83",
    classroomTableDepth: "0.76"
  };
  
  const imperialDefaults = {
    chairWidth: "1.64",
    chairDepth: "1.97",
    tableSize: "6",
    classroomTableLength: "6",
    classroomTableDepth: "2.5"
  };
  
  // Advanced parameters with defaults
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [chairWidth, setChairWidth] = useState(metricDefaults.chairWidth);
  const [chairDepth, setChairDepth] = useState(metricDefaults.chairDepth);
  const [tableSize, setTableSize] = useState(metricDefaults.tableSize);
  const [chairsBanquet, setChairsBanquet] = useState("10");
  const [chairsCabaret, setChairsCabaret] = useState("8");
  const [classroomTableLength, setClassroomTableLength] = useState(metricDefaults.classroomTableLength);
  const [classroomTableDepth, setClassroomTableDepth] = useState(metricDefaults.classroomTableDepth);
  const [chairsPerClassroom, setChairsPerClassroom] = useState("2.5");
  
  const [result, setResult] = useState(() => loadStoredState()?.result ?? null);
  const [loading, setLoading] = useState(false);

  // ---- Estimate Room Size (reverse calculator) state ----
  const [estGuestCount, setEstGuestCount] = useState(() => loadStoredState()?.estGuestCount ?? "");
  const [estSeatingStyle, setEstSeatingStyle] = useState(() => loadStoredState()?.estSeatingStyle ?? "theatre");
  const [estHasStagingAV, setEstHasStagingAV] = useState(() => loadStoredState()?.estHasStagingAV ?? false);
  const [estResult, setEstResult] = useState(() => loadStoredState()?.estResult ?? null);
  const [estLoading, setEstLoading] = useState(false);

  // ---- Compare Layouts state ----
  const [compareResult, setCompareResult] = useState(() => loadStoredState()?.compareResult ?? null);
  const [compareLoading, setCompareLoading] = useState(false);

  const navigate = useNavigate();

  // Persist key state to sessionStorage on every change so the user can come back
  // (e.g., from Floor Plan via the back button) without re-entering data.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          length,
          width,
          isMetric,
          hasStagingAV,
          result,
          estGuestCount,
          estSeatingStyle,
          estHasStagingAV,
          estResult,
          compareResult,
        })
      );
    } catch {
      /* ignore quota / disabled-storage errors */
    }
  }, [
    length,
    width,
    isMetric,
    hasStagingAV,
    result,
    estGuestCount,
    estSeatingStyle,
    estHasStagingAV,
    estResult,
    compareResult,
  ]);

  // Map a layout label (e.g., "Banquet + Dancefloor") to the URL param value
  const layoutToParam = (label) => {
    const key = label.toLowerCase().replace(/\s*\+\s*/g, "-").replace(/\s+/g, "-");
    // Known: theatre, cabaret, classroom, banquet, banquet-dancefloor
    return key;
  };

  // Layouts that Floor Plan auto-place understands (must match autoPlaceRoomLayout)
  const SUPPORTED_FLOOR_PLAN_LAYOUTS = new Set([
    "theatre",
    "cabaret",
    "classroom",
    "banquet",
    "banquet-dancefloor",
  ]);

  // Normalise a backend seating_style key (e.g., "banquet_dancefloor") to URL param
  const seatingStyleToParam = (style) => style.replace(/_/g, "-");

  // Send room + chosen layout to the Floor Plan tool
  const sendToFloorPlan = (layoutLabel, capacity) => {
    if (!result) return;
    const params = new URLSearchParams({
      room_w: String(result.room_width_m),
      room_l: String(result.room_length_m),
      layout: layoutToParam(layoutLabel),
      guests: String(capacity),
      stage: result.has_staging_av ? "1" : "0",
    });
    navigate(`/planning/floor-plan?${params.toString()}`);
  };

  // Send a comparison row (uses its own suggested dimensions) to Floor Plan
  const sendCompareRowToFloorPlan = (row) => {
    if (!compareResult) return;
    const params = new URLSearchParams({
      room_w: String(row.suggested_dimensions.width_m),
      room_l: String(row.suggested_dimensions.length_m),
      layout: seatingStyleToParam(row.seating_style),
      guests: String(compareResult.guest_count),
      stage: compareResult.has_staging_av ? "1" : "0",
    });
    navigate(`/planning/floor-plan?${params.toString()}`);
  };

  // Update defaults when unit system changes
  const handleUnitChange = (metric) => {
    setIsMetric(metric);
    const defaults = metric ? metricDefaults : imperialDefaults;
    setChairWidth(defaults.chairWidth);
    setChairDepth(defaults.chairDepth);
    setTableSize(defaults.tableSize);
    setClassroomTableLength(defaults.classroomTableLength);
    setClassroomTableDepth(defaults.classroomTableDepth);
    // Clear results when unit changes
    setResult(null);
  };

  const handleCalculate = async () => {
    if (!length || !width) {
      toast.error("Please enter room dimensions");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate/room-capacity`, {
        length: parseFloat(length),
        width: parseFloat(width),
        is_metric: isMetric,
        has_staging_av: hasStagingAV,
        chair_width: parseFloat(chairWidth) || 0.5,
        chair_depth: parseFloat(chairDepth) || 0.6,
        table_size: parseFloat(tableSize) || 1.83,
        chairs_banquet: parseInt(chairsBanquet) || 10,
        chairs_cabaret: parseInt(chairsCabaret) || 8,
        classroom_table_length: parseFloat(classroomTableLength) || 1.83,
        classroom_table_depth: parseFloat(classroomTableDepth) || 0.76,
        chairs_per_classroom: parseFloat(chairsPerClassroom) || 2.5
      });
      setResult(response.data);
      toast.success("Capacities calculated");
    } catch (error) {
      toast.error("Calculation failed");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const unit = isMetric ? "m" : "ft";

  const handleEstimateSize = async () => {
    const n = parseInt(estGuestCount);
    if (!n || n < 1) {
      toast.error("Please enter a guest count");
      return;
    }
    setEstLoading(true);
    try {
      const response = await axios.post(`${API}/calculate/room-size`, {
        guest_count: n,
        seating_style: estSeatingStyle,
        has_staging_av: estHasStagingAV,
      });
      setEstResult(response.data);
      toast.success("Room size estimated");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Calculation failed");
      console.error(error);
    } finally {
      setEstLoading(false);
    }
  };

  const handleCompareLayouts = async () => {
    const n = parseInt(estGuestCount);
    if (!n || n < 1) {
      toast.error("Please enter a guest count to compare");
      return;
    }
    setCompareLoading(true);
    try {
      const response = await axios.post(`${API}/calculate/room-size/compare`, {
        guest_count: n,
        has_staging_av: estHasStagingAV,
      });
      setCompareResult(response.data);
      toast.success("Layouts compared");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Comparison failed");
      console.error(error);
    } finally {
      setCompareLoading(false);
    }
  };

  const seatingStyleOptions = [
    { value: "theatre", label: "Theatre" },
    { value: "classroom", label: "Classroom" },
    { value: "banquet", label: "Banquet (rounds of 10)" },
    { value: "cabaret", label: "Cabaret" },
    { value: "banquet_dancefloor", label: "Banquet + Dancefloor" },
    { value: "boardroom", label: "Boardroom" },
    { value: "u_shape", label: "U-Shape" },
    { value: "hollow_square", label: "Hollow Square" },
    { value: "reception", label: "Reception (standing)" },
  ];

  return (
    <Layout 
      title="Room Capacity Calculator" 
      subtitle="Calculate maximum attendees for multiple layout types"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Input Card */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <Calculator className="h-5 w-5 text-cyan-400" />
              Room Dimensions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Toggles Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Unit Toggle */}
              <div className="p-3 bg-secondary/50 rounded-sm">
                <p className="text-sm font-medium mb-3 text-center">Unit System</p>
                <div className="flex items-center justify-center gap-3">
                  <span className={`text-sm ${!isMetric ? 'text-foreground' : 'text-muted-foreground'}`}>
                    Imperial (ft)
                  </span>
                  <Switch
                    checked={isMetric}
                    onCheckedChange={handleUnitChange}
                    data-testid="unit-toggle"
                  />
                  <span className={`text-sm ${isMetric ? 'text-foreground' : 'text-muted-foreground'}`}>
                    Metric (m)
                  </span>
                </div>
              </div>

              {/* Staging/AV Toggle */}
              <div className="p-3 bg-secondary/50 rounded-sm">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Monitor className="h-4 w-4 text-cyan-400" />
                  <p className="text-sm font-medium">Staging/AV Required</p>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <span className={`text-sm ${!hasStagingAV ? 'text-foreground' : 'text-muted-foreground'}`}>
                    No
                  </span>
                  <Switch
                    checked={hasStagingAV}
                    onCheckedChange={setHasStagingAV}
                    data-testid="staging-toggle"
                  />
                  <span className={`text-sm ${hasStagingAV ? 'text-foreground' : 'text-muted-foreground'}`}>
                    Yes
                  </span>
                </div>
              </div>
            </div>

            {/* Room Dimensions */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="length" className="label-tactical">
                  Room Length ({unit})
                </Label>
                <Input
                  id="length"
                  type="number"
                  placeholder="0.00"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  className="tactical-input"
                  data-testid="length-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="width" className="label-tactical">
                  Room Width ({unit})
                </Label>
                <Input
                  id="width"
                  type="number"
                  placeholder="0.00"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  className="tactical-input"
                  data-testid="width-input"
                />
              </div>
            </div>

            {/* Advanced Parameters Toggle */}
            <Button
              variant="ghost"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full justify-between text-muted-foreground hover:text-foreground"
              data-testid="advanced-toggle"
            >
              <span>Advanced Parameters (Chair & Table Sizes)</span>
              {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>

            {/* Advanced Parameters */}
            {showAdvanced && (
              <div className="space-y-4 p-4 bg-secondary/30 rounded-sm">
                {/* Chair Dimensions */}
                <div>
                  <p className="label-tactical text-cyan-400 mb-3">Chair Dimensions</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-tactical">Chair Width ({unit})</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={chairWidth}
                        onChange={(e) => setChairWidth(e.target.value)}
                        className="tactical-input"
                        data-testid="chair-width-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-tactical">Chair Depth ({unit})</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={chairDepth}
                        onChange={(e) => setChairDepth(e.target.value)}
                        className="tactical-input"
                        data-testid="chair-depth-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Banquet/Cabaret Table */}
                <div>
                  <p className="label-tactical text-cyan-400 mb-3">Round Table (Banquet/Cabaret)</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="label-tactical">Table Diameter ({unit})</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={tableSize}
                        onChange={(e) => setTableSize(e.target.value)}
                        className="tactical-input"
                        data-testid="table-size-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-tactical">Chairs (Banquet)</Label>
                      <Input
                        type="number"
                        value={chairsBanquet}
                        onChange={(e) => setChairsBanquet(e.target.value)}
                        className="tactical-input"
                        data-testid="chairs-banquet-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-tactical">Chairs (Cabaret)</Label>
                      <Input
                        type="number"
                        value={chairsCabaret}
                        onChange={(e) => setChairsCabaret(e.target.value)}
                        className="tactical-input"
                        data-testid="chairs-cabaret-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Classroom Table */}
                <div>
                  <p className="label-tactical text-cyan-400 mb-3">Classroom Table</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="label-tactical">Table Length ({unit})</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={classroomTableLength}
                        onChange={(e) => setClassroomTableLength(e.target.value)}
                        className="tactical-input"
                        data-testid="classroom-length-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-tactical">Table Depth ({unit})</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={classroomTableDepth}
                        onChange={(e) => setClassroomTableDepth(e.target.value)}
                        className="tactical-input"
                        data-testid="classroom-depth-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-tactical">Chairs per Table</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={chairsPerClassroom}
                        onChange={(e) => setChairsPerClassroom(e.target.value)}
                        className="tactical-input"
                        data-testid="chairs-classroom-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Button 
              onClick={handleCalculate} 
              className="tactical-btn w-full bg-cyan-600 hover:bg-cyan-700"
              disabled={loading}
              data-testid="calculate-button"
            >
              {loading ? "Calculating..." : "Calculate All Capacities"}
            </Button>
          </CardContent>
        </Card>

        {/* Result Card */}
        {result && (
          <Card className="tactical-card border-cyan-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg uppercase text-cyan-400">
                <Users className="h-5 w-5" />
                Capacity Results
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Room Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-secondary/50 rounded-sm text-center">
                  <p className="label-tactical mb-1">Room Size</p>
                  <p className="text-lg font-mono font-bold">
                    {isMetric ? `${result.room_length_m} × ${result.room_width_m} m` : 
                      `${(result.room_length_m * 3.281).toFixed(1)} × ${(result.room_width_m * 3.281).toFixed(1)} ft`}
                  </p>
                </div>
                <div className="p-3 bg-secondary/50 rounded-sm text-center">
                  <p className="label-tactical mb-1">Area</p>
                  <p className="text-lg font-mono font-bold">
                    {isMetric ? `${result.area_sqm} m²` : `${result.area_sqft} ft²`}
                  </p>
                </div>
                <div className="p-3 bg-secondary/50 rounded-sm text-center">
                  <p className="label-tactical mb-1">Staging/AV</p>
                  <p className={`text-lg font-bold ${result.has_staging_av ? 'text-cyan-400' : 'text-muted-foreground'}`}>
                    {result.has_staging_av ? "Yes" : "No"}
                  </p>
                </div>
                <div className="p-3 bg-secondary/50 rounded-sm text-center">
                  <p className="label-tactical mb-1">Max Capacity</p>
                  <p className="text-lg font-mono font-bold text-cyan-400">
                    {Math.max(...result.capacities.map(c => c.capacity))}
                  </p>
                </div>
              </div>

              {/* Capacities Table */}
              <div className="rounded-sm border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/50">
                      <TableHead className="text-foreground font-bold uppercase tracking-wider">Layout Type</TableHead>
                      <TableHead className="text-foreground font-bold uppercase tracking-wider text-right">Capacity</TableHead>
                      <TableHead className="text-foreground font-bold uppercase tracking-wider text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.capacities.map((item, index) => (
                      <TableRow key={index} className="hover:bg-secondary/30">
                        <TableCell className="font-medium">{item.layout}</TableCell>
                        <TableCell className="text-right">
                          <span className="text-2xl font-mono font-bold text-cyan-400" data-testid={`capacity-${item.layout.toLowerCase().replace(/\s+/g, '-')}`}>
                            {item.capacity}
                          </span>
                          <span className="text-muted-foreground ml-2">people</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => sendToFloorPlan(item.layout, item.capacity)}
                            data-testid={`send-floor-plan-${item.layout.toLowerCase().replace(/\s+/g, '-').replace(/\+/g, '')}`}
                          >
                            <LayoutGrid className="h-4 w-4 mr-1" />
                            Floor Plan
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Parameters Used */}
              {showAdvanced && result.parameters && (
                <div className="p-4 bg-secondary/30 rounded-sm">
                  <p className="label-tactical text-muted-foreground mb-2">Parameters Used</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
                    <span>Chair: {result.parameters.chair_width_m}×{result.parameters.chair_depth_m}m</span>
                    <span>Round Table: {result.parameters.table_size_m}m dia</span>
                    <span>Banquet: {result.parameters.chairs_banquet} chairs</span>
                    <span>Cabaret: {result.parameters.chairs_cabaret} chairs</span>
                    <span>Classroom Table: {result.parameters.classroom_table_length_m}×{result.parameters.classroom_table_depth_m}m</span>
                    <span>Classroom: {result.parameters.chairs_per_classroom} chairs/table</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Reverse Calculator: Estimate Room Size from Guest Count */}
        <Card className="tactical-card" data-testid="estimate-room-size-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <Maximize2 className="h-5 w-5 text-cyan-400" />
              Estimate Room Size from Guests
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Enter the number of guests and the seating style to get a recommended room area.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="est-guest-count" className="label-tactical">Guest Count</Label>
                <Input
                  id="est-guest-count"
                  type="number"
                  min="1"
                  placeholder="e.g. 100"
                  value={estGuestCount}
                  onChange={(e) => setEstGuestCount(e.target.value)}
                  className="tactical-input"
                  data-testid="est-guest-count-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="label-tactical">Seating Style</Label>
                <Select value={estSeatingStyle} onValueChange={setEstSeatingStyle}>
                  <SelectTrigger className="tactical-input" data-testid="est-seating-style-select">
                    <SelectValue placeholder="Select seating style" />
                  </SelectTrigger>
                  <SelectContent>
                    {seatingStyleOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} data-testid={`est-style-${opt.value}`}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-3 bg-secondary/50 rounded-sm flex flex-col justify-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Monitor className="h-4 w-4 text-cyan-400" />
                  <p className="text-sm font-medium">Staging / AV</p>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <span className={`text-sm ${!estHasStagingAV ? 'text-foreground' : 'text-muted-foreground'}`}>No</span>
                  <Switch
                    checked={estHasStagingAV}
                    onCheckedChange={setEstHasStagingAV}
                    data-testid="est-staging-toggle"
                  />
                  <span className={`text-sm ${estHasStagingAV ? 'text-foreground' : 'text-muted-foreground'}`}>Yes</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                onClick={handleEstimateSize}
                className="tactical-btn w-full bg-cyan-600 hover:bg-cyan-700"
                disabled={estLoading}
                data-testid="estimate-room-size-button"
              >
                {estLoading ? "Estimating..." : "Estimate Selected Layout"}
              </Button>
              <Button
                onClick={handleCompareLayouts}
                variant="outline"
                className="tactical-btn w-full"
                disabled={compareLoading}
                data-testid="compare-layouts-button"
              >
                {compareLoading ? "Comparing..." : "Compare All Layouts"}
              </Button>
            </div>

            {estResult && (
              <div className="space-y-4 pt-2" data-testid="estimate-room-size-result">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-secondary/50 rounded-sm text-center">
                    <p className="label-tactical mb-1">Guests</p>
                    <p className="text-lg font-mono font-bold">{estResult.guest_count}</p>
                  </div>
                  <div className="p-3 bg-secondary/50 rounded-sm text-center">
                    <p className="label-tactical mb-1">Style</p>
                    <p className="text-sm font-bold">{estResult.seating_style_label}</p>
                  </div>
                  <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-sm text-center">
                    <p className="label-tactical mb-1">Required Area</p>
                    <p className="text-2xl font-mono font-bold text-cyan-400" data-testid="est-area-sqm">
                      {estResult.required_area.sqm} m²
                    </p>
                    <p className="text-xs text-muted-foreground font-mono" data-testid="est-area-sqft">
                      {estResult.required_area.sqft} ft²
                    </p>
                  </div>
                  <div className="p-3 bg-secondary/50 rounded-sm text-center">
                    <p className="label-tactical mb-1">Suggested Room</p>
                    <p className="text-base font-mono font-bold" data-testid="est-dim-m">
                      {estResult.suggested_dimensions.length_m} × {estResult.suggested_dimensions.width_m} m
                    </p>
                    <p className="text-xs text-muted-foreground font-mono" data-testid="est-dim-ft">
                      {estResult.suggested_dimensions.length_ft} × {estResult.suggested_dimensions.width_ft} ft
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-secondary/30 rounded-sm text-xs text-muted-foreground">
                  <p>
                    <span className="font-semibold text-foreground">Per person:</span>{" "}
                    {estResult.per_person.sqm} m² / {estResult.per_person.sqft} ft² ·{" "}
                    <span className="font-semibold text-foreground">Notes:</span> {estResult.notes}
                  </p>
                </div>
              </div>
            )}

            {compareResult && (
              <div className="space-y-3 pt-2 border-t border-border/40" data-testid="compare-layouts-result">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm uppercase font-semibold tracking-wide">
                    Layout Comparison — {compareResult.guest_count} Guests
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {compareResult.has_staging_av
                      ? `Includes +${compareResult.staging_adjustment_pct}% for staging/AV`
                      : "No staging/AV adjustment"}{" "}
                    · Sorted by area (smallest first)
                  </p>
                </div>

                <div className="rounded-sm border border-border/40 overflow-x-auto">
                  <Table data-testid="compare-layouts-table">
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="uppercase text-xs">Layout</TableHead>
                        <TableHead className="uppercase text-xs text-right">Per Person</TableHead>
                        <TableHead className="uppercase text-xs text-right">Area (m²)</TableHead>
                        <TableHead className="uppercase text-xs text-right">Area (ft²)</TableHead>
                        <TableHead className="uppercase text-xs text-right">Suggested L × W</TableHead>
                        <TableHead className="uppercase text-xs text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {compareResult.layouts.map((l, idx) => (
                        <TableRow
                          key={l.seating_style}
                          className={idx === 0 ? "bg-cyan-500/5" : ""}
                          data-testid={`compare-row-${l.seating_style}`}
                        >
                          <TableCell className="font-medium">
                            {l.seating_style_label}
                            {idx === 0 && (
                              <span className="ml-2 text-[10px] uppercase tracking-wider text-cyan-400 font-bold">
                                most efficient
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs text-muted-foreground">
                            {l.per_person.sqm} m² / {l.per_person.sqft} ft²
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold">
                            {l.required_area.sqm}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {l.required_area.sqft}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            {l.suggested_dimensions.length_m} × {l.suggested_dimensions.width_m} m
                            <br />
                            <span className="text-muted-foreground">
                              {l.suggested_dimensions.length_ft} × {l.suggested_dimensions.width_ft} ft
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {SUPPORTED_FLOOR_PLAN_LAYOUTS.has(seatingStyleToParam(l.seating_style)) ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => sendCompareRowToFloorPlan(l)}
                                data-testid={`compare-floor-plan-${l.seating_style}`}
                              >
                                <LayoutGrid className="h-3.5 w-3.5 mr-1" />
                                Floor Plan
                              </Button>
                            ) : (
                              <span className="text-[10px] text-muted-foreground italic">
                                manual layout
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Layout Guide */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="text-lg uppercase">Layout Guide</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="p-3 rounded-sm border border-border/50">
                <p className="text-sm font-semibold text-cyan-400">Theatre</p>
                <p className="text-xs text-muted-foreground">Rows of chairs facing the stage, maximum capacity</p>
              </div>
              <div className="p-3 rounded-sm border border-border/50">
                <p className="text-sm font-semibold text-cyan-400">Cabaret</p>
                <p className="text-xs text-muted-foreground">Round tables with chairs on one side facing stage</p>
              </div>
              <div className="p-3 rounded-sm border border-border/50">
                <p className="text-sm font-semibold text-cyan-400">Classroom</p>
                <p className="text-xs text-muted-foreground">Rectangular tables with chairs on one side</p>
              </div>
              <div className="p-3 rounded-sm border border-border/50">
                <p className="text-sm font-semibold text-cyan-400">Banquet</p>
                <p className="text-xs text-muted-foreground">Round tables with chairs all around for dining</p>
              </div>
              <div className="p-3 rounded-sm border border-border/50">
                <p className="text-sm font-semibold text-cyan-400">Banquet + Dancefloor</p>
                <p className="text-xs text-muted-foreground">Banquet layout with 13% space reserved for dancing</p>
              </div>
              <div className="p-3 rounded-sm border border-border/50">
                <p className="text-sm font-semibold text-yellow-400">Staging/AV</p>
                <p className="text-xs text-muted-foreground">Reduces usable length by 25% for stage setup</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default RoomCapacity;
